import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { publicAPI, imagesAPI } from '../../lib/api';
import { useBooking } from '../../contexts/BookingContext';
import { ImageGallery } from '../../components/ImageGallery';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { format, addDays } from 'date-fns';
import { cn } from '../../lib/utils';
import {
  Building2,
  MapPin,
  Mail,
  CalendarIcon,
  Users,
  BedDouble,
  Loader2,
  ArrowLeft,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateSearch, setSearchResults, setSelectedRoom } = useBooking();

  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [searchResults, setLocalSearchResults] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await publicAPI.getProperty(id);
        setProperty(response.data);
      } catch (error) {
        toast.error('Property not found');
        navigate('/properties');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id, navigate]);

  const handleSearch = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setSearchLoading(true);
    try {
      const searchData = {
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests: parseInt(guests),
        property_id: id,
      };

      const response = await publicAPI.searchAvailability(searchData);
      setLocalSearchResults(response.data);
      updateSearch({
        checkIn: searchData.check_in,
        checkOut: searchData.check_out,
        guests: searchData.guests,
        propertyId: id,
      });
    } catch (error) {
      toast.error('Failed to search availability');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBookRoom = (room) => {
    setSelectedRoom(room);
    setSearchResults({
      ...searchResults,
      rooms: [room],
    });
    navigate('/checkout');
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  if (!property) {
    return null;
  }

  return (
    <PublicLayout>
      <div className="py-8" data-testid="property-detail-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/properties')}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Properties
          </Button>

          {/* Property Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Gallery */}
            <div>
              <ImageGallery
                images={property.gallery?.length > 0 ? property.gallery : (property.image_url ? [property.image_url] : [])}
                alt={property.name}
                fallbackIcon={<Building2 className="h-24 w-24 text-muted-foreground" />}
              />
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <div className="mb-6">
                <h1 className="text-4xl font-serif font-medium mb-4">{property.name}</h1>
                <div className="flex items-center gap-2 text-muted-foreground mb-4">
                  <MapPin className="h-5 w-5" />
                  <span>
                    {property.address}, {property.city}, {property.country}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-5 w-5" />
                  <span>{property.contact_email}</span>
                </div>
              </div>

              {property.description && (
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {property.description}
                </p>
              )}

              {/* Search Form */}
              <div className="bg-surface rounded-xl p-6 mt-auto">
                <h3 className="font-semibold mb-4">Check Availability</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !checkIn && 'text-muted-foreground'
                        )}
                        data-testid="prop-checkin-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkIn ? format(checkIn, 'MMM dd') : 'Check-in'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkIn}
                        onSelect={(date) => {
                          setCheckIn(date);
                          setCheckInOpen(false);
                          if (!checkOut || checkOut <= date) {
                            setCheckOut(addDays(date, 1));
                          }
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !checkOut && 'text-muted-foreground'
                        )}
                        data-testid="prop-checkout-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkOut ? format(checkOut, 'MMM dd') : 'Check-out'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOut}
                        onSelect={(date) => {
                          setCheckOut(date);
                          setCheckOutOpen(false);
                        }}
                        disabled={(date) => date <= (checkIn || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Select value={guests} onValueChange={setGuests}>
                    <SelectTrigger data-testid="prop-guests-select">
                      <SelectValue placeholder="Guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'Guest' : 'Guests'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleSearch}
                  disabled={searchLoading}
                  className="w-full"
                  data-testid="prop-search-btn"
                >
                  {searchLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Search Availability
                </Button>
              </div>
            </div>
          </div>

          {/* Search Results */}
          {searchResults && (
            <div className="mb-12" data-testid="search-results">
              <h2 className="text-2xl font-serif font-medium mb-6">
                Available Rooms ({searchResults.rooms.length})
              </h2>
              <p className="text-muted-foreground mb-6">
                {format(new Date(searchResults.check_in), 'MMM dd, yyyy')} -{' '}
                {format(new Date(searchResults.check_out), 'MMM dd, yyyy')} • {searchResults.nights}{' '}
                night(s)
              </p>

              {searchResults.rooms.length === 0 ? (
                <div className="bg-surface rounded-xl p-12 text-center">
                  <BedDouble className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No rooms available</h3>
                  <p className="text-muted-foreground">
                    Try different dates or check back later.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {searchResults.rooms.map((room) => (
                    <div
                      key={room.id}
                      className="bg-white border border-border rounded-xl p-6 hover:shadow-soft transition-shadow"
                      data-testid={`room-result-${room.id}`}
                    >
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Room Image */}
                        <div className="w-full md:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
                          {(room.gallery?.length > 0 || room.image_url) ? (
                            <img
                              src={room.gallery?.length > 0 ? imagesAPI.getUrl(room.gallery[0]) : room.image_url}
                              alt={room.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              <BedDouble className="h-10 w-10 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        {/* Room Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-xl font-semibold">{room.name}</h3>
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                              {room.available_count} available
                            </Badge>
                          </div>

                          {room.description && (
                            <p className="text-muted-foreground mb-4">{room.description}</p>
                          )}

                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Users className="h-4 w-4" />
                              <span>Up to {room.max_occupancy} guests</span>
                            </div>
                            {room.amenities?.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {room.amenities.slice(0, 3).map((amenity) => (
                                  <Badge key={amenity} variant="secondary" className="text-xs">
                                    {amenity}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(room.price_per_night)} / night
                              </p>
                              <p className="text-2xl font-semibold">
                                {formatCurrency(room.total_price)}
                                <span className="text-sm font-normal text-muted-foreground">
                                  {' '}
                                  total
                                </span>
                              </p>
                            </div>
                            <Button
                              onClick={() => handleBookRoom(room)}
                              data-testid={`book-room-${room.id}`}
                            >
                              Book Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
